import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { EstateTaskInstance, useTaskCredit_GetCurrentTaskCreditQuery } from 'queries';

import { Button } from 'common/button';
import Spinner from 'common/spinner';
import PaymentForm from 'pages/common/estate/payment-form';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  setShowModal: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  replaceEstateTaskInstance: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  addEstateTaskInstance: Function;
  estateTaskInstance?: EstateTaskInstance;
};

const useStyles = makeStyles((theme) => ({
  modalContentWrapper: {
    marginTop: '20px',
    marginBottom: '20px',
    backgroundColor: 'rgba(240, 243, 246, 0.7)',
    padding: '25px',
    paddingTop: '20px',
    '& input': {
      background: 'white',
    },
  },
  cancelButton: {
    marginRight: theme.spacing(2),
  },
  modalButtonWrapper: {
    marginBottom: '20px',
  },
  assetWrapper: {
    color: '#2274A5',
  },
}));

export default function EstateTaskInstancePaymentForm({
  estateTaskInstance,
  setShowModal,
  replaceEstateTaskInstance,
  addEstateTaskInstance,
}: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: currentTaskCreditData, loading: currentTaskCreditLoading } =
    useTaskCredit_GetCurrentTaskCreditQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting current task credit: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!currentTaskCreditData || currentTaskCreditLoading) {
    return <Spinner />;
  }

  const hasPayment = !!estateTaskInstance?.estateWorkItemId;

  const handlePaymentComplete = (processedEstateTaskInstances: EstateTaskInstance[]) => {
    // update the first task
    const updatedEstateTaskInstance = processedEstateTaskInstances[0];
    replaceEstateTaskInstance(estateTaskInstance, updatedEstateTaskInstance);

    // add any new tasks
    if (processedEstateTaskInstances.length > 1) {
      for (let i = 1; i < processedEstateTaskInstances.length; i++) {
        addEstateTaskInstance(processedEstateTaskInstances[i]);
      }
    }

    setShowModal(false);
    showToast({
      message:
        processedEstateTaskInstances.length > 1
          ? 'Tasks pushed to Trustate.'
          : 'Task pushed to Trustate.',
      severity: NotificationSeverity.SUCCESS,
    });
  };

  let bodyContent: JSX.Element | undefined = undefined;
  if (estateTaskInstance) {
    bodyContent = (
      <Typography className={classes.assetWrapper} variant="h6">
        <div
          dangerouslySetInnerHTML={{
            __html: estateTaskInstance?.label || '',
          }}
        ></div>
      </Typography>
    );
  }

  return (
    <>
      <div className={classes.modalContentWrapper}>
        <PaymentForm
          allowPaymentQuantity={!!estateTaskInstance?.allowPaymentQuantity}
          bodyContent={bodyContent}
          estateTaskInstanceId={estateTaskInstance?.id}
          hasPayment={hasPayment}
          formName="estate-task-instance-form"
          handlePaymentComplete={handlePaymentComplete}
          title="Push Task to Trustate"
          portalId={portalId}
        />
      </div>
      <div className={classes.modalButtonWrapper}>
        <Button
          className={classes.cancelButton}
          color="primary"
          onClick={() => {
            setShowModal(false);
          }}
        >
          Cancel
        </Button>{' '}
        {!hasPayment && (
          <Button color="secondary" type="submit" form="estate-task-instance-form">
            Start Task
          </Button>
        )}
      </div>
    </>
  );
}
